"use client"

import { useRouter, useSearchParams } from "next/navigation"
import { DriverFilters } from "./driver-filters"
import type { ServiceName } from "@/lib/drivers/types"
import {
  toURLParam,
  cleanURLParams,
  fromURLParam,
  toURLFilterValue,
  fromURLFilterValue,
} from "@/lib/drivers/url-params"
import {
  FilterValues,
  StrictSearchParams,
  VerificationType,
  ExperienceLevel,
  WonShipmentsRange,
} from "@/lib/drivers/types"

interface DriverFiltersClientProps {
  stats: {
    verifications: {
      backgroundCheck: number
      insurance: number
      identity: number
      usdot: number
      usda: number
      manual: number
    }
    shipmentTypes: Record<ServiceName, number>
    ratings: {
      [key: number]: number
    }
    experience: {
      new: number
      intermediate: number
      expert: number
    }
    wonShipments: {
      lessThan10: number
      between10And50: number
      moreThan50: number
    }
    shipmentCategories: { [key: string]: any }
  }
  activeFilters: FilterValues
  searchParams: StrictSearchParams
}

export function DriverFiltersClient({
  stats,
  activeFilters,
  searchParams,
}: DriverFiltersClientProps) {
  const router = useRouter()
  const params = useSearchParams()

  const handleFilterChange = (updates: Partial<FilterValues>) => {
    const newParams = new URLSearchParams(params?.toString() || "")
    // Reset page when filters change
    newParams.delete(toURLParam("page"))

    // Update filter params
    Object.entries(updates).forEach(([key, value]) => {
      const urlKey = toURLParam(key)
      if (
        !value ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === "string" && value.trim() === "") ||
        (typeof value === "number" && isNaN(value))
      ) {
        newParams.delete(urlKey)
      } else if (Array.isArray(value)) {
        // Convert array values to URL-friendly format
        const urlValues = value.map((v) => toURLFilterValue(v))
        newParams.set(urlKey, urlValues.join(","))
      } else {
        newParams.set(urlKey, toURLFilterValue(value.toString()))
      }
    })

    // Clean up parameters
    const cleanParams = cleanURLParams(newParams)

    router.replace(`?${cleanParams.toString()}`, { scroll: false })
  }

  // Convert active filters to their internal values
  const internalActiveFilters: FilterValues = {
    verifications: activeFilters.verifications.map((v) =>
      fromURLFilterValue(v),
    ) as VerificationType[],
    shipmentTypes: activeFilters.shipmentTypes.map((v) =>
      fromURLFilterValue(v),
    ) as ServiceName[],
    rating: activeFilters.rating,
    experience: activeFilters.experience
      ? (fromURLFilterValue(activeFilters.experience) as ExperienceLevel)
      : null,
    wonShipments: activeFilters.wonShipments
      ? (fromURLFilterValue(activeFilters.wonShipments) as WonShipmentsRange)
      : null,
    shipmentCategories: activeFilters.shipmentCategories
      ? fromURLFilterValue(activeFilters.shipmentCategories)
      : null,
  }

  // Only log active filters when they change
  if (
    activeFilters.verifications.length > 0 ||
    activeFilters.shipmentTypes.length > 0 ||
    activeFilters.rating !== null ||
    activeFilters.experience !== null ||
    activeFilters.wonShipments !== null ||
    activeFilters.shipmentCategories != null
  ) {
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-6">
      <h2 className="text-xl font-semibold">Filters</h2>
      <DriverFilters
        filterStats={stats}
        searchParams={{
          verifications: internalActiveFilters.verifications.join(","),
          shipmentTypes: internalActiveFilters.shipmentTypes.join(","),
          rating: internalActiveFilters.rating?.toString() || "",
          experience: internalActiveFilters.experience || "",
          wonShipments: internalActiveFilters.wonShipments || "",
          shipmentCategories: internalActiveFilters.shipmentCategories || "",
        }}
        onFilterChange={handleFilterChange}
        isLoading={false}
      />
    </div>
  )
}
